import { useEffect, useRef, useState } from 'react';
import { sayAlex, stopAlex } from '@/audio/alex';
import { labBunny } from '@/content/helpers';
import { REPORT } from '@/content/reports';
import { stripTags } from '@/content/story';
import { markReported, reportedBefore } from '@/game/beats';
import type { Floor } from '@/content/floors';
import { strings } from '@/content/strings';
import { letterCount } from '@/game/codename';
import { useProgress } from '@/game/progressStore';
import { useLab } from '@/game/labProgress';
import { navigate } from '@/app/router';
import { cssVars } from '@/lib/cssVars';
import { CodenameTiles } from '@/ui/CodenameTiles';
import { Frame } from '@/ui/Frame';
import { SignalMeter } from '@/ui/SignalMeter';
import { Sparkles } from '@/ui/Sparkles';
import { StarRating } from '@/ui/StarRating';
import { Surface } from '@/ui/Surface';
import type { MissionOutcome } from './VoiceprintScanner';
import styles from './MissionComplete.module.css';

interface MissionCompleteProps {
  floor: Floor;
  outcome: MissionOutcome;
  onElevator: () => void;
  onAgain: () => void;
}

/**
 * End of a mission, as a page of its own (docs/design/transmission.md): the foe, unmasked once the
 * codename is complete, large beside (or above) the results: the stars earned (popping in one by
 * one) and the transmission bonus apart from them, what they mean, the child's own progress, and
 * the way back to the elevator.
 */
export function MissionComplete({ floor, outcome, onElevator, onAgain }: MissionCompleteProps) {
  const t = strings.mission;
  const {
    stars,
    improved,
    unlockedFloor,
    result,
    revealed,
    previousBest,
    practised,
    signalFrom,
    bonus,
  } = outcome;
  // The r signal now (read live: the mission's last attempt may be recorded just after it ends).
  const signal = useProgress()[floor.id]?.mastery?.p;
  const lab = useLab();
  // Progress against the child's own best, never against others.
  const own =
    previousBest === null
      ? null
      : result.correct > previousBest
        ? t.beatBest(result.correct, previousBest)
        : result.correct === previousBest && result.correct > 0
          ? t.matchedBest(result.correct)
          : t.keepPractising(previousBest);
  // 2 or more stars uncover the whole codename and show who the foe really is.
  const unmasked = revealed >= letterCount(floor.foe.codename);
  const primary = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    primary.current?.focus();
  }, []);
  // Agent Alex opens the report: what the stars mean the first time, then a short, honest line
  // (QA 2026-10-11: the jump from the puzzle to the results was abrupt).
  const [line] = useState(() => {
    const bank = !reportedBefore()
      ? REPORT.first
      : unlockedFloor
        ? REPORT.unlocked
        : stars >= 2
          ? REPORT.passed
          : REPORT.practice;
    return bank[Math.floor(Math.random() * bank.length)] ?? '';
  });
  useEffect(() => {
    markReported();
    void sayAlex(line);
    return () => {
      stopAlex();
    };
  }, [line]);
  const label = t.starLabels[Math.max(0, stars - 1)] ?? t.starLabels[0];
  const hint = t.starHints[Math.max(0, stars - 1)] ?? '';

  return (
    <div
      className={styles.page}
      role="dialog"
      aria-modal="true"
      aria-labelledby="mission-title"
      style={cssVars({ '--floor-color': floor.color })}
    >
      <h2 id="mission-title" className={styles.title}>
        {t.complete}
      </h2>
      <p className={styles.alex} aria-live="polite">
        <img src={labBunny.portraits.clipboard.url} alt="" draggable={false} />
        <span>{stripTags(line)}</span>
      </p>
      <div className={styles.layout}>
        <div className={styles.hero}>
          <div className={styles.foe}>
            <img
              src={(unmasked ? floor.foe.portraits.revealed : floor.foe.portraits.disguised).url}
              alt=""
              draggable={false}
            />
            {unmasked && <Sparkles />}
          </div>
          <CodenameTiles codename={floor.foe.codename} revealedCount={revealed} />
        </div>
        <Frame className={styles.panel}>
          <Surface variant="screen" className={styles.screen}>
            <StarRating stars={stars} size={44} animate className={styles.stars} />
            {bonus && (
              // Apart from the mission stars, which keep their meaning (they open floors).
              <p className={styles.bonus}>
                <span aria-hidden>★</span> {strings.transmission.bonus}
              </p>
            )}
            <p className={styles.label}>
              {label}
              {improved && <span className={styles.best}>{t.newBest}</span>}
            </p>
            <p className={styles.detail}>
              {strings.mission.wordsRight(result.correct, result.targets)}
            </p>
            {own && <p className={styles.detail}>{own}</p>}
            {result.attempts !== undefined && result.attempts > 0 && (
              <p className={styles.detail}>{t.practised(result.attempts, practised)}</p>
            )}
            {signal !== undefined && (
              <div className={styles.signal}>
                <SignalMeter value={signal} from={signalFrom} />
                <p className={styles.detail}>{t.signal(signalFrom, signal)}</p>
              </div>
            )}
            {result.bestStreak !== undefined && result.bestStreak >= 3 && (
              <p className={styles.detail}>{t.streak(result.bestStreak)}</p>
            )}
            {unlockedFloor ? (
              <p className={styles.unlocked}>{t.unlocked(unlockedFloor)}</p>
            ) : (
              stars < 3 && <p className={styles.hint}>{hint}</p>
            )}
            <div className={styles.actions}>
              <button ref={primary} type="button" className={styles.primary} onClick={onElevator}>
                {t.elevator}
              </button>
              <button type="button" className={styles.secondary} onClick={onAgain}>
                {t.again}
              </button>
              {/* Some words were tricky: the lab shows what happens inside (alex-lab.md). */}
              {lab.reveal === 'accepted' && result.correct < result.targets && (
                <button
                  type="button"
                  className={styles.secondary}
                  onClick={() => {
                    navigate({ page: 'lab' });
                  }}
                >
                  Look inside with Alex
                </button>
              )}
            </div>
          </Surface>
        </Frame>
      </div>
    </div>
  );
}
